import { Box } from '@mui/material'
import React, { type ReactNode } from 'react'
import { Select } from 'shared/ui/Select/Select'
import {
  Error,
  type IButtonProps,
  type IDropdownItem,
  type IDropdownProps,
  type IDropdownListProps,
} from 'shared/ui'
import { type ILabelProps, Label } from 'shared/ui/Label'
import { type ISelectDefaultProps } from 'shared/ui/Select/SelectDefault/SelectDefault'
import { getAriaLabel } from 'shared/lib'

export type ISelectFieldProps<T = string> = {
  labelText?: string
  labelProps?: ILabelProps
  boxWidth?: string
  items: IDropdownItem[]
  value?: T
  item?: IDropdownItem | null
  placeholder?: ReactNode
  setValue: (value: T, item: IDropdownItem) => void
  selectProps?: Partial<ISelectDefaultProps>
  btnProps?: IButtonProps
  labelGap?: string
  dropdownProps?: IDropdownProps
  onSearch?: IDropdownListProps['onSearch']
  search?: IDropdownListProps['search']
  onLoadMore?: IDropdownListProps['onLoadMore']
  loading?: IDropdownListProps['loading']
  error?: ReactNode
  withSearch?: boolean
  disabled?: boolean
  size?: 'small' | 'medium'
  className?: string
  parentsDropdownId?: string[]
}

export const SelectField = ({
  labelText = '',
  labelProps,
  boxWidth,
  items,
  placeholder,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  value,
  item,
  setValue,
  selectProps,
  btnProps,
  labelGap = '4px',
  dropdownProps,
  onSearch,
  search,
  withSearch = true,
  onLoadMore,
  loading,
  error,
  disabled,
  size = 'small',
  className,
  parentsDropdownId,
}: ISelectFieldProps<typeof value>) => (
  <Box
    className={className}
    display={'grid'}
    gap={labelGap}
    aria-label={getAriaLabel(`SelectField${labelText}`)}
    {...(boxWidth && { width: boxWidth })}
  >
    {(labelText || labelProps) && (
      <Label
        label={{
          text: labelText,
        }}
        {...labelProps}
      />
    )}
    <Select
      type={'default'}
      placeholder={placeholder}
      value={value}
      item={item}
      items={items}
      onSelectValue={setValue}
      error={error}
      fullWidth
      btnProps={{
        fullWidth: true,
        typeBtn: 'stroke',
        justifyContent: 'space-between',
        contained: undefined,
        fontWeight: 400,
        style: {
          color: !(value || value === 0) && !item ? 'var(--content-primary-disabled)' : undefined,
          background: disabled ? undefined : 'var(--background-primary-inverted-primary)',
          minHeight: size === 'medium' ? 40 : 32,
        },
        disabled,
        ...btnProps,
      }}
      dropdownProps={{
        margin: 0,
        ariaLabel: 'SelectField',
        PopperProps: {
          modifiers: [
            {
              name: 'offset',
              options: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                offset: ({ reference }) => {
                  return [-4, -reference.height]
                },
              },
            },
          ],
        },
        disabled,
        ...dropdownProps,
        propsDropdownList: {
          onSearch,
          search,
          onLoadMore,
          loading,
          withSearch,
          placeholder: 'Search',
          ...dropdownProps?.propsDropdownList,
        },
      }}
      parentsDropdownId={parentsDropdownId}
      {...selectProps}
    />
    {error && <Error type={'text'} error={error} />}
  </Box>
)
